import React, { useEffect, useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle2, XCircle, Save, Flag, AlertTriangle } from "lucide-react";

export default function ExamReviewPage() {
  useSeo({ title: "Exam grading", noindex: true });
  const { attemptId } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualScores, setManualScores] = useState({});
  const [status, setStatus] = useState("graded");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) nav("/login");
    if (!authLoading && user && user.role !== "admin" && user.role !== "instructor") nav("/dashboard");
  }, [authLoading, user, nav]);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "instructor")) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/exam-attempts/${attemptId}`);
        setData(data);
        setManualScores(data.attempt.manual_scores || {});
        setStatus(data.attempt.status === "in_progress" ? "graded" : data.attempt.status);
        setNote(data.attempt.admin_note || "");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load attempt");
      } finally {
        setLoading(false);
      }
    })();
  }, [attemptId, user]);

  const totals = useMemo(() => {
    if (!data) return { awarded: 0, available: 0, percent: 0 };
    const auto = data.attempt.auto_score || 0;
    let manualAwarded = 0;
    for (const v of Object.values(manualScores)) manualAwarded += Number(v?.points || 0);
    const essayAvail = data.exam.questions.filter((q) => q.type === "essay").reduce((a,q) => a + (Number(q.points)||0), 0);
    const available = (data.attempt.auto_total || 0) + essayAvail;
    const awarded = auto + manualAwarded;
    const percent = available > 0 ? Math.round((awarded / available) * 100) : 0;
    return { awarded, available, percent };
  }, [data, manualScores]);

  const save = async () => {
    const passMark = data.exam.pass_mark_percent ?? 70;
    if (status === "passed" && totals.percent < passMark &&
        !window.confirm(`Score is ${totals.percent}%, below the ${passMark}% pass mark. Mark as PASS anyway?`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/exam-attempts/${attemptId}/grade`, {
        manual_scores: manualScores,
        final_status: status,
        admin_note: note,
      });
      toast.success("Grade saved");
      nav("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">Loading attempt…</div>;
  if (!data) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">Attempt not found</div>;

  const { attempt, exam, course } = data;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Link to="/admin" className="chip inline-flex items-center gap-1 mb-6"><ChevronLeft size={13}/> Back to admin</Link>
      <div className="eyebrow text-[var(--gold-soft)]">Exam grading</div>
      <h1 className="font-display text-2xl sm:text-3xl text-[var(--navy)]">{exam.title}</h1>
      <p className="text-sm text-zinc-600 mt-1">
        {course?.title} · Candidate: <b>{attempt.user_name}</b> ({attempt.user_email})
      </p>
      <p className="text-xs text-zinc-500 mt-1">
        Started {(attempt.started_at || "").slice(0,16).replace("T"," ")} ·
        Submitted {(attempt.submitted_at || "").slice(0,16).replace("T"," ")} ·
        Auto {attempt.auto_score}/{attempt.auto_total}
      </p>
      <button
        onClick={async () => {
          try {
            const res = await api.get(`/admin/exam-attempts/${attemptId}/transcript`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `csi-transcript-${attempt.user_email.split("@")[0]}.pdf`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); a.remove();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Transcript download failed");
          }
        }}
        className="chip mt-3 inline-flex items-center gap-1 text-xs"
        data-testid="download-transcript"
      >
        <Save size={13}/> Download transcript PDF
      </button>

      {/* Integrity feed */}
      {(attempt.honour_code_name || attempt.integrity_events?.length > 0) && (
        <div className="mt-5 card-warm p-4" data-testid="integrity-panel">
          {attempt.honour_code_name && (
            <div className="mb-3" data-testid="honour-code-record">
              <div className="eyebrow text-[var(--gold-soft)]">Honour-code declaration</div>
              <div className="text-sm text-[var(--navy)] mt-1 font-mono">
                Signed by <b>{attempt.honour_code_name}</b> on {(attempt.honour_code_signed_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
          )}
          {(attempt.integrity_events || []).length > 0 && (
            <div className="border-t border-[var(--border-warm)] pt-3" data-testid="integrity-feed">
              <div className="eyebrow inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={13}/> Integrity events ({attempt.integrity_events.length})</div>
              <ul className="text-xs text-zinc-700 mt-2 space-y-1 max-h-40 overflow-y-auto">
                {attempt.integrity_events.map((e, i) => (
                  <li key={i} className="font-mono">
                    <span className="text-zinc-500">{(e.at||"").slice(11,19)}</span> · <b>{e.type}</b>
                    {e.detail ? ` - ${e.detail}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Questions + manual scoring */}
      <div className="mt-6 space-y-4">
        {exam.questions.map((q, i) => {
          const ans = attempt.answers?.[q.id];
          const auto = q.type !== "essay";
          const isCorrect = auto && ans?.selected_index === q.correct_index;
          return (
            <div key={q.id} className="card-warm p-5" data-testid={`review-q-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="eyebrow">Q{i+1} · {q.type.toUpperCase()} · {q.points} pt</div>
                  <h3 className="font-display text-lg text-[var(--navy)] whitespace-pre-line">{q.prompt}</h3>
                </div>
                {auto && (
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${isCorrect ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    {isCorrect ? <CheckCircle2 size={12}/> : <XCircle size={12}/>} {isCorrect ? "Correct" : "Wrong"}
                  </span>
                )}
              </div>
              {q.type !== "essay" ? (
                <div className="mt-3 space-y-1 text-sm">
                  {(q.options || []).map((opt, idx) => {
                    const picked = ans?.selected_index === idx;
                    const correct = q.correct_index === idx;
                    return (
                      <div key={idx} className={`p-2 rounded-lg border text-xs ${correct ? "border-emerald-300 bg-emerald-50" : picked ? "border-red-300 bg-red-50" : "border-[var(--border-warm)]"}`}>
                        <b>{picked ? "► " : correct ? "✓ " : "  "}</b>{opt}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-3">
                  <div className="text-xs text-zinc-500 mb-1">Candidate answer</div>
                  <pre className="whitespace-pre-wrap text-sm bg-[var(--muted-surface)] p-3 rounded-xl font-mono max-h-60 overflow-y-auto" data-testid={`review-essay-${q.id}`}>
{ans?.essay_text || "(no answer provided)"}
                  </pre>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {(ans?.essay_text || "").trim().split(/\s+/).filter(Boolean).length} words
                  </div>
                  {q.rubric && (
                    <details className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3" open data-testid={`rubric-${q.id}`}>
                      <summary className="text-xs font-bold text-amber-900 cursor-pointer">Grading rubric</summary>
                      <div className="text-xs text-amber-900 mt-2 whitespace-pre-wrap">{q.rubric}</div>
                    </details>
                  )}
                  <div className="mt-3 grid sm:grid-cols-[auto_1fr] gap-2 items-start">
                    <div>
                      <label className="eyebrow block mb-1">Award (0–{q.points})</label>
                      <input
                        type="number"
                        min={0}
                        max={q.points}
                        value={manualScores[q.id]?.points ?? ""}
                        onChange={(e) => setManualScores((p) => ({ ...p, [q.id]: { ...(p[q.id]||{}), points: Math.max(0, Math.min(q.points, Number(e.target.value||0))) } }))}
                        className="w-24 p-2 border border-[var(--border-warm)] rounded-lg font-mono"
                        data-testid={`grade-points-${q.id}`}
                      />
                    </div>
                    <div>
                      <label className="eyebrow block mb-1">Feedback (optional)</label>
                      <textarea
                        value={manualScores[q.id]?.feedback || ""}
                        onChange={(e) => setManualScores((p) => ({ ...p, [q.id]: { ...(p[q.id]||{}), feedback: e.target.value } }))}
                        className="w-full p-2 border border-[var(--border-warm)] rounded-lg text-sm"
                        rows={2}
                        data-testid={`grade-feedback-${q.id}`}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals + final status */}
      <div className="mt-8 card-warm p-5 sticky bottom-4">
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <div className="eyebrow">Total</div>
            <div className="font-display text-3xl text-[var(--navy)]" data-testid="grade-total">
              {totals.awarded}/{totals.available} · {totals.percent}%
            </div>
            {exam.pass_mark_percent != null && (
              <div className="text-[11px] text-zinc-500 mt-1">Reference pass mark: {exam.pass_mark_percent}%</div>
            )}
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="eyebrow block mb-1">Final status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full p-2 border border-[var(--border-warm)] rounded-lg" data-testid="grade-status">
              <option value="graded">Graded (show score only)</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="submitted">Return to pending</option>
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="eyebrow block mb-1">Internal note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full p-2 border border-[var(--border-warm)] rounded-lg text-sm" data-testid="grade-note"/>
          </div>
          <button onClick={save} disabled={saving} className="btn-terracotta px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="grade-save">
            <Save size={14}/> Save grade
          </button>
        </div>
      </div>
    </div>
  );
}
